import { assertCanWriteSchedule, type UserRole } from "@/app/userRole";
import { apiClient, isHttpClientError } from "@/http";
import { apiRoutes } from "./apiRoutes";
import type { RegistryDatasetRef, SubmitTrainingSessionReq } from "./training";

/**
 * 예약 실행(스케줄) API. 같은 제출을 반복해서 등록하는 경로다.
 *
 * ZERG-535 에서 채택한 API 다. Operations > Schedule 페이지(목록·편집)와 "새 Schedule" 위저드가
 * 이 API 를 쓴다. 스케줄은 research 공용 workspace 에서만 만들어진다. 그래서 목록의 정본은
 * `listOperationsSchedules`(전역, 자격 게이트)다. `experiment_id` 가 필수인 `listTrainingSchedules`
 * 는 스코프 도달성 확인 경로로 남는다(스코프 밖과 부재가 같은 404).
 *
 * 제출 전문(`submit`)은 단건 제출과 **같은 타입**이다. 스케줄은 그 제출에 두 가지만 더한다:
 * - 언제 반복할지
 * - 발화 게이트 3축(branch 무변화 skip · 유휴 GPU · eval 릴리스 추종)
 * 그래서 위저드가 만든 payload 를 그대로 보낸다.
 *
 * 쓰기(생성·수정·pause/resume·해제)는 **admin 전용**이다(BE `training.schedule_write_forbidden`
 * 미러). 해제는 상류 soft-cancel 이라 되돌릴 수 없다.
 */

// 반복 축. `intervalSeconds` 와 `cron` 중 **정확히 하나**만 채운다(BE 가 422 로 거른다).
// `timezone` 은 cron 에만 쓴다. cron 은 local wall-clock 반복 규칙이라 UTC 로 환산하지 않는다.
export type TrainingScheduleRecurrence = {
  intervalSeconds?: number;
  cron?: string;
  timezone?: string;
};

export type CreateTrainingScheduleReq = TrainingScheduleRecurrence & {
  name: string;
  description?: string;
  startAt?: string;
  endAt?: string;
  // 발화 게이트 3축. 지정하지 않으면 키를 보내지 않는다. 상류 기본값과 같은 값을 보내면 노이즈다.
  // skip 은 branch learning-core recipe 가 전제다. 릴리스 추종은 활성 eval scenario 가 전제다.
  // 전제 판정은 상류가 소유한다. 전제를 어기면 400 `upstream.invalid_request` 가 온다.
  skipIfUnchanged?: boolean;
  minFreeGpus?: number;
  trackLatestEvalRelease?: boolean;
  submit: SubmitTrainingSessionReq;
};

// 서버는 PATCH 에서 보낸 필드만 적용한다. `minFreeGpus` 만 3-상태다:
//   - undefined: 유지
//   - 값: 설정
//   - **명시 null: 게이트 해제**
// bool 두 축에는 해제 상태가 없다. undefined=유지 / 값=설정 두 상태뿐이다.
export type UpdateTrainingScheduleReq = TrainingScheduleRecurrence & {
  name?: string;
  description?: string;
  endAt?: string;
  skipIfUnchanged?: boolean;
  minFreeGpus?: number | null;
  trackLatestEvalRelease?: boolean;
};

// 상류 어휘를 그대로 쓴다. registry enum 으로 제한하지 않는다.
export type TrainingScheduleStatus = "active" | "paused" | "cancelled";

// ---- recipe 요약. 목록의 구성 열이 읽는 파생 값(세션 상세의 `recipe` 원문과 다름) ----

// 입력 dataset 1건. registry 등록 dataset 은 원본 참조로 온다. training 에서 만든 dataset 은
// 실제 이름으로 온다. 둘 다 없으면 training 전용 표기를 쓴다(등록 이름을 대신 표시하지 않는다).
// 같은 dataset 을 역할만 바꿔 두 번 넣을 수 있다. 그래서 항목은 이름으로 유일하지 않다.
export type TrainingScheduleRecipeDataset = {
  registry_ref?: RegistryDatasetRef | null;
  upstream_dataset_label?: string | null;
  version?: string | null;
  role: string;
};

// config 의 출처. yaml 본문은 계약에 없다.
export type TrainingScheduleRecipeConfig = {
  source?: string | null;
  name?: string | null;
};

// learning-core 지정. branch 면 발화할 때마다 서버가 tip 으로 resolve 한다.
export type TrainingScheduleRecipeLearningCore = {
  kind?: string | null;
  ref?: string | null;
  repo?: string | null;
};

export type TrainingScheduleRecipeSummary = {
  datasets: TrainingScheduleRecipeDataset[];
  config?: TrainingScheduleRecipeConfig | null;
  learning_core?: TrainingScheduleRecipeLearningCore | null;
  has_eval: boolean;
};

// 스케줄에 저장된 실행 설정을 **생성 입력으로 복원한** 값(`GET .../recipe`). 목록의 요약과
// 달리 config 본문·override·eval scenario 까지 온다. BE 는 다음 값을 되돌린다:
//   - 등록 이름(`reg-ds-*`) → Registry UUID
//   - mask 의 annotation 경로 → 세트 UUID
// 그래서 `recipe` 를 그대로 생성 요청의 `submit` 에 넣을 수 있다(단건 제출과 같은 타입이다).
//
// 복원되지 않는 값이 있다. BE 는 저장하지 않는 `channel_selection`·검증 digest·experiment
// 표시명을 추정하지 않는다. `dataset_filter` 제출은 확정된 `datasets` 목록으로 펼쳐서 온다.
// 복원할 수 없는 설정이 있으면 BE 는 일부만 빼지 않는다. 409
// `training.schedule_recipe_incompatible` 로 전체를 거절한다.
export type TrainingScheduleRecipeView = {
  recipe: SubmitTrainingSessionReq;
  submit_schema_version: number;
};

// `skip_if_unchanged`/`min_free_gpus`/`track_latest_eval_release` 는 발화 게이트 설정이다.
// `last_resolved_*`/`last_deferred_*` 는 그 게이트가 남긴 관측값이다. 상류는 `schedule_recipe` 를
// 항상 보낸다. 요약 안의 `config`·`learning_core` 는 null 일 수 있지만 요약 자체는 항상 있다.
export type TrainingScheduleView = {
  id: string;
  experiment_id: string;
  name: string;
  description?: string | null;
  status: string;
  interval_seconds?: number | null;
  cron?: string | null;
  timezone?: string | null;
  start_at?: string | null;
  end_at?: string | null;
  next_run_at?: string | null;
  last_run_at?: string | null;
  last_session_id?: string | null;
  last_error?: string | null;
  run_count: number;
  skip_if_unchanged: boolean;
  min_free_gpus?: number | null;
  track_latest_eval_release: boolean;
  last_resolved_sha?: string | null;
  last_resolved_eval_image?: string | null;
  last_deferred_at?: string | null;
  last_defer_reason?: string | null;
  schedule_recipe: TrainingScheduleRecipeSummary;
  created_at?: string | null;
};

// 발화 1건. `session_id` 는 그 발화가 만든 세션이다(발화 전·실패·건너뜀이면 null).
// `skipped` 는 branch tip 이 바뀌지 않아 제출을 건너뛴 회차다. 그 회차에만 `resolved_sha` 가 있다.
export type TrainingScheduleOccurrenceView = {
  id: string;
  scheduled_for: string;
  status: string;
  session_id?: string | null;
  error?: string | null;
  resolved_sha?: string | null;
  created_at?: string | null;
};

// Operations 목록 항목. 스케줄 view 와, 발화로 만들어져 아직 실행 중인 세션 수를 담는다.
export type TrainingScheduleOperationsItem = TrainingScheduleView & {
  active_session_count: number;
};

export type TrainingScheduleOperationsView = {
  items: TrainingScheduleOperationsItem[];
};

// snake_case 경계 변환은 여기서만 한다. 화면은 camelCase 만 쓰고 서버 이름을 알 필요가 없다.
function toRecurrencePayload(recurrence: TrainingScheduleRecurrence): Record<string, unknown> {
  return {
    ...(recurrence.intervalSeconds === undefined
      ? {}
      : { interval_seconds: recurrence.intervalSeconds }),
    ...(recurrence.cron === undefined ? {} : { cron: recurrence.cron }),
    ...(recurrence.timezone === undefined ? {} : { timezone: recurrence.timezone }),
  };
}

export async function createTrainingSchedule(
  request: CreateTrainingScheduleReq,
  role: UserRole | null,
): Promise<TrainingScheduleView> {
  assertCanWriteSchedule(role);
  const response = await apiClient.post<TrainingScheduleView>(apiRoutes.training.schedules, {
    name: request.name,
    ...(request.description === undefined ? {} : { description: request.description }),
    ...toRecurrencePayload(request),
    ...(request.startAt === undefined ? {} : { start_at: request.startAt }),
    ...(request.endAt === undefined ? {} : { end_at: request.endAt }),
    ...(request.skipIfUnchanged === undefined
      ? {}
      : { skip_if_unchanged: request.skipIfUnchanged }),
    ...(request.minFreeGpus === undefined ? {} : { min_free_gpus: request.minFreeGpus }),
    ...(request.trackLatestEvalRelease === undefined
      ? {}
      : { track_latest_eval_release: request.trackLatestEvalRelease }),
    submit: request.submit,
  });
  return response.data;
}

// 실험 하나의 스케줄 전부. 서버 페이징이 없는 전체 응답이라 화면이 그대로 보관한다.
export async function listTrainingSchedules(experimentId: string): Promise<TrainingScheduleView[]> {
  const response = await apiClient.get<TrainingScheduleView[]>(apiRoutes.training.schedules, {
    params: { experiment_id: experimentId },
  });
  return response.data;
}

/**
 * Operations > Schedule 목록. 전역 스케줄과 스케줄별 실행 중 세션 수를 조회한다.
 *
 * 오류는 다음과 같다:
 * - 자격(admin 또는 research 멤버)이 없으면 403 `training.schedule_operations_forbidden`
 * - 상류가 무필터 counts 를 아직 제공하지 않으면 503 `upstream.unavailable`
 * 화면은 둘을 구분하지 않고 `resolveLoadErrorMessage` 의 문구로 표시한다. 자격이 없는 사람은
 * 페이지 게이트가 먼저 막으므로 여기까지 오지 않는다.
 */
export async function listOperationsSchedules(): Promise<TrainingScheduleOperationsView> {
  const response = await apiClient.get<TrainingScheduleOperationsView>(
    apiRoutes.training.scheduleOperations,
  );
  return response.data;
}

export async function getTrainingSchedule(scheduleId: string): Promise<TrainingScheduleView> {
  const response = await apiClient.get<TrainingScheduleView>(
    apiRoutes.training.schedule(scheduleId),
  );
  return response.data;
}

export async function getTrainingScheduleRecipe(
  scheduleId: string,
): Promise<TrainingScheduleRecipeView> {
  const response = await apiClient.get<TrainingScheduleRecipeView>(
    apiRoutes.training.scheduleRecipe(scheduleId),
  );
  return response.data;
}

// 서버는 보낸 필드만 적용한다. 반복 축의 XOR 재계산은 현재 행 상태에 달려 있어 상류가 판정한다.
// `minFreeGpus` 의 명시 null 은 그대로 `null` 로 보낸다(게이트 해제). 3-상태 경계는 이 함수와
// 타입 주석이 소유한다. 호출부는 바뀐 필드만 담아 보낸다.
export async function updateTrainingSchedule(
  scheduleId: string,
  request: UpdateTrainingScheduleReq,
  role: UserRole | null,
): Promise<TrainingScheduleView> {
  assertCanWriteSchedule(role);
  const response = await apiClient.patch<TrainingScheduleView>(
    apiRoutes.training.schedule(scheduleId),
    {
      ...(request.name === undefined ? {} : { name: request.name }),
      ...(request.description === undefined ? {} : { description: request.description }),
      ...toRecurrencePayload(request),
      ...(request.endAt === undefined ? {} : { end_at: request.endAt }),
      ...(request.skipIfUnchanged === undefined
        ? {}
        : { skip_if_unchanged: request.skipIfUnchanged }),
      ...(request.minFreeGpus === undefined ? {} : { min_free_gpus: request.minFreeGpus }),
      ...(request.trackLatestEvalRelease === undefined
        ? {}
        : { track_latest_eval_release: request.trackLatestEvalRelease }),
    },
  );
  return response.data;
}

export async function pauseTrainingSchedule(
  scheduleId: string,
  role: UserRole | null,
): Promise<TrainingScheduleView> {
  assertCanWriteSchedule(role);
  const response = await apiClient.post<TrainingScheduleView>(
    apiRoutes.training.schedulePause(scheduleId),
  );
  return response.data;
}

// 재개하면 상류가 next_run_at 을 다시 계산한다. 멈춘 동안 놓친 발화를 한꺼번에 실행하지 않는다.
export async function resumeTrainingSchedule(
  scheduleId: string,
  role: UserRole | null,
): Promise<TrainingScheduleView> {
  assertCanWriteSchedule(role);
  const response = await apiClient.post<TrainingScheduleView>(
    apiRoutes.training.scheduleResume(scheduleId),
  );
  return response.data;
}

// 해제는 상류 soft-cancel 이다. 행은 남고 status 가 cancelled 가 된다(복원할 수 없다).
export async function cancelTrainingSchedule(
  scheduleId: string,
  role: UserRole | null,
): Promise<void> {
  assertCanWriteSchedule(role);
  await apiClient.delete(apiRoutes.training.schedule(scheduleId));
}

// 스케줄 쓰기 admin 게이트(403)의 코드를 판별한다. 오래된 탭(플래그 갱신 전)에서 누른 쓰기를
// 막는 안전장치다. 화면은 이 코드를 권한 문구로 바꿔 표시한다.
export function isScheduleWriteForbiddenError(error: unknown): boolean {
  return isHttpClientError(error) && error.code === "training.schedule_write_forbidden";
}

// 스코프 밖과 부재는 같은 404(training.schedule_not_found)다. 목록에 보이는 행에서 이 코드가 오면
// 스코프 문제다. admin 이어도 research 그룹에 속하지 않으면 공용 workspace 의 Schedule 에 접근할
// 수 없다.
export function isScheduleNotFoundError(error: unknown): boolean {
  return isHttpClientError(error) && error.code === "training.schedule_not_found";
}

// 저장된 설정을 생성 입력으로 복원할 수 없다(409). 다음 원인이 모두 이 한 코드로 온다:
//   - 미지원 설정·schema 버전
//   - 참조 부재·모호
//   - dataset 버전 불일치
// 다시 요청해도 결과가 같다. 그래서 재시도 대상이 아니다. 서버는 원인별 사유를 로그에만 남긴다.
// 그래서 응답 문구로는 원인을 구분할 수 없다.
export function isScheduleRecipeIncompatibleError(error: unknown): boolean {
  return isHttpClientError(error) && error.code === "training.schedule_recipe_incompatible";
}

export async function listTrainingScheduleOccurrences(
  scheduleId: string,
  limit?: number,
): Promise<TrainingScheduleOccurrenceView[]> {
  const response = await apiClient.get<TrainingScheduleOccurrenceView[]>(
    apiRoutes.training.scheduleOccurrences(scheduleId),
    limit === undefined ? undefined : { params: { limit } },
  );
  return response.data;
}
